import { useMutation, useQuery } from '@tanstack/react-query'
import { ProductsFilterParams, CreateProductParams, UpdateProductParams } from '@/types/product'
import { useProductsStore } from '@/store'
import { useBulkDeleteSuccessHandler } from './use-bulk-delete'

export const useProducts = (params: ProductsFilterParams, currency?: string) => {
  const fetchProducts = useProductsStore(state => state.fetchProducts)

  return useQuery({
    queryKey: ['products', params, currency],
    queryFn: () => fetchProducts(params, currency),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  })
}

export const useProduct = (id: string, currency?: string) => {
  const getProduct = useProductsStore(state => state.getProduct)

  return useQuery({
    queryKey: ['product', id, currency],
    queryFn: () => getProduct(id, currency),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id
  })
}

export const useCreateProduct = () => {
  const createProduct = useProductsStore(state => state.createProduct)

  return useMutation({
    mutationFn: (params: CreateProductParams) => createProduct(params),
    onSuccess: () => {}
  })
}

export const useUpdateProduct = () => {
  const updateProduct = useProductsStore(state => state.updateProduct)

  return useMutation({
    mutationFn: ({ id, params }: { id: string; params: UpdateProductParams }) => updateProduct(id, params),
    onSuccess: () => {}
  })
}

export const useDeleteProduct = () => {
  const deleteProduct = useProductsStore(state => state.deleteProduct)

  return useMutation({
    mutationFn: (id: string) => deleteProduct(id),
    onSuccess: () => {}
  })
}

export const useBulkDeleteProducts = () => {
  const bulkDeleteProducts = useProductsStore(state => state.bulkDeleteProducts)
  const deselectAllProducts = useProductsStore(state => state.deselectAllProducts)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteProducts(ids),
    onSuccess: response => {
      deselectAllProducts()
      handleBulkDeleteSuccess(response)
    }
  })
}
